import React from 'react';

// Maps each shipment status to a label + DS state-token pair — all ten values of
// Status Model v2 (docs/09-FE-CHANGES.md §1). Anything else the gateway sends
// falls back to a neutral, title-cased badge.
const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  CREATED: { label: 'Created', bg: 'bg-information-lighter', text: 'text-information-dark' },
  AT_HUB: { label: 'At Hub', bg: 'bg-highlighted-lighter', text: 'text-highlighted-dark' },
  PARTIALLY_DISPATCHED: { label: 'Partially Dispatched', bg: 'bg-away-lighter', text: 'text-away-dark' },
  DISPATCHED: { label: 'Dispatched', bg: 'bg-away-lighter', text: 'text-away-dark' },
  IN_DELIVERY: { label: 'In Delivery', bg: 'bg-feature-lighter', text: 'text-feature-dark' },
  IN_DISPUTE: { label: 'In Dispute', bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  COMPLETED: { label: 'Completed', bg: 'bg-success-lighter', text: 'text-success-dark' },
  // Nothing on the waybill was delivered — part of the "return" family with
  // In Dispute, hence the same amber tone.
  RETURNED: { label: 'Returned', bg: 'bg-warning-lighter', text: 'text-warning-dark' },
  // Active items exhausted with at least one LOST.
  FAILED: { label: 'Failed', bg: 'bg-error-lighter', text: 'text-error-dark' },
  CANCELLED: { label: 'Cancelled', bg: 'bg-faded-lighter', text: 'text-faded-dark' },
};

const toTitleCase = (status: string) =>
  status
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Human-readable label for a shipment status (used by the status filter too).
export const getShipmentStatusLabel = (status: string): string =>
  STATUS_CONFIG[status]?.label ?? toTitleCase(status || 'Unknown');

interface ShipmentStatusBadgeProps {
  status: string;
  className?: string;
}

export const ShipmentStatusBadge: React.FC<ShipmentStatusBadgeProps> = ({
  status,
  className = '',
}) => {
  const config = STATUS_CONFIG[status] ?? {
    label: toTitleCase(status || 'Unknown'),
    bg: 'bg-bg-weak-50',
    text: 'text-text-sub-600',
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text} ${className}`.trim()}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {config.label}
    </span>
  );
};

export default ShipmentStatusBadge;
